/*
 * <install-location-selector>
 */
if (!customElements.get('install-location-selector')) {
  customElements.define(
    'install-location-selector',
    class InstallLocationSelector extends HTMLElement {
      constructor() {
        super();

        this.button = this.querySelector('.disclosure__button');
        this.panel = this.querySelector('.disclosure__list-wrapper');
        this.options = Array.from(this.querySelectorAll('.disclosure__link'));
        this.input = this.querySelector('input[data-install-location-input]');
        this.labelSpan = this.button
          ? this.button.querySelector('.install-location__selection') || this.button.querySelector('span')
          : null;
        this.errorMessage = this.closest('.install-location')?.querySelector('[data-install-location-error]');

        if (!this.button || !this.panel || !this.input) return;

        this.onButtonClick = this.onButtonClick.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);

        this.button.addEventListener('click', this.onButtonClick);
        this.options.forEach((option) => {
          option.addEventListener('click', this.onOptionClick.bind(this));
        });

        this.addEventListener('keydown', this.onKeyDown);
        document.addEventListener('click', this.onDocumentClick);
      }

      disconnectedCallback() {
        this.button?.removeEventListener('click', this.onButtonClick);
        document.removeEventListener('click', this.onDocumentClick);
        this.removeEventListener('keydown', this.onKeyDown);
      }

      get value() {
        return this.input.disabled ? '' : this.input.value;
      }

      get isOpen() {
        return this.button.getAttribute('aria-expanded') === 'true';
      }

      get panelScope() {
        return this.closest('product-info') || this.closest('.configurator__aside') || this;
      }

      onButtonClick(event) {
        event.stopPropagation();
        this.toggle();
      }

      toggle() {
        if (this.isOpen) {
          this.hide(true);
          return;
        }

        DisclosurePanel.closeSiblings(this.panel, this.panelScope);
        DisclosurePanel.show(this.panel, this.button);
        this.focusOption(this.getSelectedOption() || this.options[0]);
      }

      show() {
        DisclosurePanel.closeSiblings(this.panel, this.panelScope);
        DisclosurePanel.show(this.panel, this.button);
        this.focusOption(this.getSelectedOption() || this.options[0]);
      }

      hide(refocusButton = false) {
        DisclosurePanel.hide(this.panel, this.button);
        if (refocusButton) this.button.focus();
      }

      onDocumentClick(event) {
        if (!this.contains(event.target)) this.hide();
      }

      onOptionClick(event) {
        this.select(event.currentTarget);
      }

      getSelectedOption() {
        return this.options.find((option) => option.getAttribute('aria-selected') === 'true');
      }

      getCharges(taxableCents) {
        const option = this.getSelectedOption();
        if (!option) return null;

        const shippingCents = Number.parseInt(option.dataset.shippingCents, 10);
        const shipping = Number.isFinite(shippingCents) ? shippingCents : 0;
        const taxRate = Number.parseFloat(option.dataset.salesTaxRate || '0') || 0;
        const taxCents = Math.round((Number(taxableCents || 0) + shipping) * taxRate / 100);

        return {
          shippingCents: shipping,
          taxCents,
          taxRate,
          shippingProperty: `$${(shipping / 100).toFixed(2)}`,
          taxProperty: `$${(taxCents / 100).toFixed(2)} (${taxRate}%)`,
        };
      }

      focusOption(option) {
        option?.focus({ preventScroll: true });
      }

      select(option, options = {}) {
        if (!option) return;

        this.options.forEach((item) => {
          item.setAttribute('aria-selected', 'false');
          item.classList.remove('selected');
        });
        option.setAttribute('aria-selected', 'true');
        option.classList.add('selected');

        const label = option.dataset.locationName || option.textContent.trim();
        const value = option.dataset.value || label;

        this.renderSelection(option);
        this.input.value = value;
        this.input.disabled = false;
        this.setErrorState(false);

        this.hide(!options.silent);

        this.dispatchEvent(
          new CustomEvent('installLocationSelected', {
            bubbles: true,
            detail: {
              value,
              label,
              handle: option.dataset.handle || '',
              locationName: option.dataset.locationName || '',
              salesTaxRate: option.dataset.salesTaxRate || '',
              shippingCents: option.dataset.shippingCents || '0',
              shippingLabel: option.dataset.shippingLabel || '',
            },
          })
        );
      }

      renderSelection(option) {
        if (!this.labelSpan) return;

        const placeholder = this.labelSpan.getAttribute('data-label') || 'Please Select';
        if (!option) {
          this.labelSpan.textContent = placeholder;
          this.button?.removeAttribute('aria-label');
          return;
        }

        const name = option.dataset.locationName || option.textContent.trim();
        const taxLabel = option.dataset.taxLabel || '';
        const shippingLabel = option.dataset.shippingLabel || '';
        this.labelSpan.replaceChildren();

        const nameEl = document.createElement('span');
        nameEl.className = 'install-location__name';
        nameEl.textContent = name;
        this.labelSpan.appendChild(nameEl);

        if (taxLabel || shippingLabel) {
          const meta = document.createElement('span');
          meta.className = 'install-location__meta';

          if (taxLabel) {
            const taxFlag = document.createElement('span');
            taxFlag.className = 'install-location__flag';
            taxFlag.textContent = taxLabel;
            meta.appendChild(taxFlag);
          }

          if (taxLabel && shippingLabel) {
            const sep = document.createElement('span');
            sep.className = 'install-location__sep';
            sep.setAttribute('aria-hidden', 'true');
            sep.textContent = '·';
            meta.appendChild(sep);
          }

          if (shippingLabel) {
            const shippingFlag = document.createElement('span');
            shippingFlag.className = 'install-location__flag';
            shippingFlag.textContent = shippingLabel;
            meta.appendChild(shippingFlag);
          }

          this.labelSpan.appendChild(meta);
        }

        this.button?.setAttribute('aria-label', [name, taxLabel, shippingLabel].filter(Boolean).join(', '));
      }

      setErrorState(hasError, message = 'Please select an install location.') {
        this.button.classList.toggle('error', hasError);
        this.button.setAttribute('aria-invalid', hasError ? 'true' : 'false');

        if (!this.errorMessage) return;

        this.errorMessage.textContent = hasError ? message : '';
        this.errorMessage.classList.toggle('visually-hidden', !hasError);
      }

      onKeyDown(event) {
        const key = event.code?.toUpperCase() || event.key;

        if (key === 'ESCAPE') {
          if (!this.isOpen) return;
          event.preventDefault();
          event.stopPropagation();
          this.hide(true);
          return;
        }

        if (!this.isOpen) {
          if ((key === 'ARROWDOWN' || key === 'ARROWUP' || key === 'SPACE') && document.activeElement === this.button) {
            event.preventDefault();
            this.show();
          }
          return;
        }

        const visibleOptions = this.options.filter((option) => option.closest('.disclosure__item')?.style.display !== 'none');
        const currentIndex = visibleOptions.indexOf(document.activeElement);

        switch (key) {
          case 'ARROWDOWN':
            event.preventDefault();
            this.focusOption(
              currentIndex < visibleOptions.length - 1 ? visibleOptions[currentIndex + 1] : visibleOptions[0]
            );
            break;
          case 'ARROWUP':
            event.preventDefault();
            this.focusOption(
              currentIndex > 0 ? visibleOptions[currentIndex - 1] : visibleOptions[visibleOptions.length - 1]
            );
            break;
          case 'HOME':
            event.preventDefault();
            this.focusOption(visibleOptions[0]);
            break;
          case 'END':
            event.preventDefault();
            this.focusOption(visibleOptions[visibleOptions.length - 1]);
            break;
          case 'ENTER':
          case 'SPACE':
            if (currentIndex > -1) {
              event.preventDefault();
              this.select(visibleOptions[currentIndex]);
            }
            break;
          case 'TAB':
            this.hide();
            break;
          default:
            break;
        }
      }
    }
  );
}
